import type { UIMessage } from '@ai-sdk/react';
import type { ChatStatus } from 'ai';
import { parseAsBoolean, useQueryState } from 'nuqs';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { AIChatMarkdownComponents, AIChatMarkdownVariant } from '../AIChatMarkdown';
import { AIChatMessagesContext, type AIChatMessagesContextValue } from './context';

export type AIChatMessagesProps = {
  children: ReactNode;
  markdownComponents?: AIChatMarkdownComponents;
  markdownVariant?: AIChatMarkdownVariant;
  messageContentClassName?: string;
  hideAssistantMessage?: boolean;
  hideUserMessage?: boolean;
  hideTools?: boolean;
  hideAvatar?: boolean;
  hideReasoning?: boolean;
  messages: UIMessage[];
  status: ChatStatus;
  onRegenerate: () => void;
  source?: string;
};

const AIChatMessages = ({
  children,
  messages,
  status,
  markdownComponents,
  markdownVariant = 'chat',
  messageContentClassName = '',
  hideAvatar = true,
  hideAssistantMessage = false,
  hideUserMessage = false,
  hideTools = false,
  hideReasoning = false,
  onRegenerate,
  source = 'sidepanel',
}: AIChatMessagesProps) => {
  const [isDebug] = useQueryState('debug', parseAsBoolean.withDefault(false));

  const lastMessage = messages?.[messages.length - 1] || { role: 'assistant', parts: [] };

  if (isDebug) {
    console.log('messages', messages); // Don't remove this console.
  }

  const value = useMemo<AIChatMessagesContextValue>(
    () => ({
      isDebug,
      lastMessageId: lastMessage?.id,
      messages,
      status,
      markdownComponents,
      markdownVariant,
      messageContentClassName,
      hideAssistantMessage,
      hideAvatar,
      hideReasoning,
      hideTools,
      hideUserMessage,
      onRegenerate,
      source,
    }),
    [
      isDebug,
      lastMessage?.id,
      messages,
      status,
      markdownComponents,
      markdownVariant,
      messageContentClassName,
      hideAssistantMessage,
      hideAvatar,
      hideReasoning,
      hideTools,
      hideUserMessage,
      onRegenerate,
      source,
    ]
  );

  return <AIChatMessagesContext.Provider value={value}>{children}</AIChatMessagesContext.Provider>;
};

export default AIChatMessages;
